// RiskRegisterPage — toont alle open soft-warnings + hard-blocks uit validate.
// Per risico: mitigation-veld + status-dropdown. Lokaal state; sync bij blur.

import React, { useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Stack,
    TextField,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useWizardContext } from './WizardPage.jsx';
import { api, endpoints } from '../api/client.js';

const STATUS_OPTIONS = [
    { value: 'open', labelKey: 'risk.status.open', color: 'error' },
    { value: 'accepted', labelKey: 'risk.status.accepted', color: 'warning' },
    { value: 'mitigated', labelKey: 'risk.status.mitigated', color: 'success' },
];

const SEVERITY_LABEL = { hard_block: 'Blokkerend', soft_warning: 'Waarschuwing' };

export default function RiskRegisterPage() {
    const { t } = useTranslation();
    const { risks, setRisks, reloadRisks, riskLoading } = useWizardContext();
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState(null);

    const patchRisk = async (riskId, patch) => {
        const before = risks.find(r => r.id === riskId);
        setRisks(prev => prev.map(r => r.id === riskId ? { ...r, ...patch } : r));
        setSavingId(riskId);
        setError(null);
        try {
            const saved = await api.patch(endpoints.risk(riskId), patch);
            setRisks(prev => prev.map(r => r.id === riskId ? { ...saved, cluster: saved.cluster_id } : r));
        } catch (err) {
            // Niet opgeslagen: zet de oude waarde terug zodat het scherm klopt.
            setRisks(prev => prev.map(r => r.id === riskId && before ? before : r));
            setError(`Wijziging niet opgeslagen: ${err?.message || 'geen verbinding'}`);
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Box>
            <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
            >
                <Typography variant="h1">{t('risk.title')}</Typography>
                <Tooltip title="Opnieuw valideren">
                    <span>
                        <IconButton onClick={reloadRisks} disabled={riskLoading} aria-label="Opnieuw valideren">
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {risks.length === 0 && (
                <Alert severity="success">{t('risk.empty')}</Alert>
            )}

            <Stack spacing={2}>
                {risks.map(risk => {
                    return (
                        <Card key={risk.id} variant="outlined">
                            <CardContent>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                                    sx={{ mb: 2 }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                            {risk.cluster && (
                                                <Chip label={`Cluster ${risk.cluster}`} size="small" color="primary" variant="outlined" />
                                            )}
                                            {risk.item_id && (
                                                <Chip label={risk.item_id.replace(/@\d+$/, '')} size="small" variant="outlined" />
                                            )}
                                            {risk.variant_label && (
                                                <Chip
                                                    label={risk.relevant === false ? `${risk.variant_label} (geen voorkeur)` : risk.variant_label}
                                                    size="small"
                                                    variant={risk.relevant === false ? 'outlined' : 'filled'}
                                                />
                                            )}
                                            <Chip
                                                label={SEVERITY_LABEL[risk.severity] || risk.severity}
                                                size="small"
                                                color={risk.severity === 'hard_block' ? 'error' : 'warning'}
                                            />
                                        </Stack>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {risk.message}
                                        </Typography>
                                        {risk.rule_name && (
                                            <Typography variant="caption" color="text.secondary">
                                                Regel: {risk.rule_name}
                                            </Typography>
                                        )}
                                    </Box>
                                    <TextField
                                        label={t('risk.status')}
                                        value={risk.status}
                                        onChange={(e) => patchRisk(risk.id, { status: e.target.value })}
                                        select
                                        size="small"
                                        fullWidth={false}
                                        sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                                        disabled={savingId === risk.id}
                                    >
                                        {STATUS_OPTIONS.map(o => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {t(o.labelKey)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                                <TextField
                                    label={t('risk.mitigation')}
                                    value={risk.mitigation || ''}
                                    onChange={(e) =>
                                        setRisks(prev => prev.map(r => r.id === risk.id
                                            ? { ...r, mitigation: e.target.value }
                                            : r))
                                    }
                                    onBlur={(e) => patchRisk(risk.id, { mitigation: e.target.value })}
                                    helperText="Wordt opgeslagen zodra je het veld verlaat"
                                    inputProps={{ maxLength: 4000 }}
                                    multiline
                                    minRows={2}
                                    fullWidth
                                />
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
}
